import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import api from '../../services/api';

const HelpContext = createContext(null);

/**
 * HelpProvider — global state for the help ecosystem:
 *   • sidebar open/close
 *   • current page slug (derived by the sidebar from useLocation)
 *   • launching tours via Driver.js
 *   • tracking which tours the user has completed (persisted in localStorage)
 */
export const HelpProvider = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSlug, setActiveSlug] = useState(null);

  const openSidebar = useCallback((slug) => {
    if (slug) setActiveSlug(slug);
    setSidebarOpen(true);
  }, []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  // ── Tour completion bookkeeping (localStorage) ─────────────────────────
  const COMPLETED_KEY = 'helpToursCompleted';
  const getCompleted = () => {
    try { return JSON.parse(localStorage.getItem(COMPLETED_KEY) || '{}'); } catch { return {}; }
  };
  const markTourCompleted = (slug, tourName) => {
    const c = getCompleted();
    c[`${slug}:${tourName}`] = new Date().toISOString();
    try { localStorage.setItem(COMPLETED_KEY, JSON.stringify(c)); } catch {}
  };
  const isTourCompleted = (slug, tourName) => !!getCompleted()[`${slug}:${tourName}`];

  // Read once per page load and remembered: this is consulted on every page
  // change, and a request per navigation to answer the same question would be
  // a tax on every screen. A failed read defaults to SHOWING them — the tours
  // are the helpful state, and a network blip must not silently switch off a
  // feature somebody is relying on.
  const toursPref = useRef(null);
  const toursEnabled = useCallback(async () => {
    if (toursPref.current !== null) return toursPref.current;
    try {
      const r = await api.get('/users/me', { validateStatus: (s) => s < 500 });
      toursPref.current = r.status === 200
        ? r.data?.show_guided_tours !== false
        : true;
    } catch {
      toursPref.current = true;
    }
    return toursPref.current;
  }, []);

  // Called by the account page when the switch is flipped, so the change takes
  // effect without a reload.
  const setToursEnabled = useCallback((on) => { toursPref.current = !!on; }, []);

  // ── Launch a tour by slug (fetches tour steps from the backend) ────────
  const startTour = useCallback(async (slug, tourName = null) => {
    try {
      const resp = await api.get(`/help/tours/${slug}`);
      const tours = resp.data?.tours || [];
      if (tours.length === 0) return;
      const tour = tourName ? tours.find(t => t.name === tourName) : tours[0];
      if (!tour) return;

      const d = driver({
        showProgress: true,
        allowClose: true,
        overlayOpacity: 0.55,
        steps: tour.steps.map((s) => ({
          element: s.selector || undefined,
          popover: {
            title: s.title,
            description: s.body,
            side: s.side || 'bottom',
            align: s.align || 'start',
          },
        })),
        onDestroyed: () => markTourCompleted(slug, tour.name),
      });
      d.drive();
    } catch (err) {
      console.error('Failed to start tour:', err);
    }
  }, []);

  // ── First-visit auto-launch (called by HelpSidebar when page changes) ──
  // Silent — 404 just means the page has no help content authored yet, which is fine.
  const maybeAutoLaunchFirstVisitTour = useCallback(async (slug) => {
    try {
      // THE ACCOUNT'S OWN ANSWER FIRST. Completion is remembered in
      // localStorage, so a cleared cache or a shared workstation used to
      // restart the tours for somebody who had already sat through them —
      // or never wanted them. `show_guided_tours` is on the user row and
      // travels with them.
      //
      // Only the AUTOMATIC launch is governed. `startTour` still runs when
      // somebody asks for a tour from the help sidebar: turning off the
      // uninvited one should not take away the one you went looking for.
      if (!(await toursEnabled())) return;
      const resp = await api.get(`/help/tours/${slug}`, { validateStatus: (s) => s < 500 });
      if (resp.status !== 200) return;
      const tours = resp.data?.tours || [];
      const first = tours.find(t => t.trigger === 'first_visit');
      if (!first) return;
      if (isTourCompleted(slug, first.name)) return;
      setTimeout(() => startTour(slug, first.name), 600);
    } catch {}
  }, [startTour]);

  const value = useMemo(() => ({
    sidebarOpen, openSidebar, closeSidebar,
    activeSlug, setActiveSlug,
    startTour, maybeAutoLaunchFirstVisitTour,
    isTourCompleted, toursEnabled, setToursEnabled,
  }), [sidebarOpen, activeSlug, openSidebar, closeSidebar, startTour,
       maybeAutoLaunchFirstVisitTour, toursEnabled, setToursEnabled]);

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
};

export const useHelp = () => {
  const ctx = useContext(HelpContext);
  if (!ctx) throw new Error('useHelp must be used inside a HelpProvider');
  return ctx;
};
